/**
 * BookingAuditTimeline — "Activity" card showing a booking's action-provenance
 * trail (BON-143): comp/manual/self-service creation, rebooking (incl. cutoff
 * override), and refunds, newest first. Reads `GET /api/v1/audit/bookings/:id`.
 * Lives in the main column beneath Attendees (moved from the right rail per a
 * manual-testing follow-up to BON-335) — `LoadedBooking` owns the placement.
 *
 * This is the interim surface for the trail so it's reviewable now; BON-319 owns
 * the richer Timeline/Audit panel. Renders a loading/empty state rather than
 * disappearing, so the section is always locatable on the page.
 *
 * `eventIcon` and `auditRow` are plain (lowercase) functions invoked as
 * function calls, not JSX tags — pre-existing `react/no-multi-comp` violation
 * fixed while this file was open for the BON-335 follow-ups above. The
 * pattern (used verbatim in `RefundBookingDrawer.tsx`) is that a lowercase
 * name can't be written as a JSX tag at all, so ESLint's component detection
 * never flags it: each is used exactly once, from exactly one caller, with no
 * reuse for a separate file to buy.
 */
import {
  ArrowUpRight,
  DollarSign,
  Mail,
  MailWarning,
  PlusCircle,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  fetchBookingAudit,
  type BookingAuditEntry,
  type RebookedContext,
  type RefundIssuedContext,
} from '../../api/bookingAudit';

const cardCls =
  'bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700';

/**
 * `data-testid` on every branch (not just the newest two) rather than an
 * icon-only selector: `lucide-react` here replaces its default `lucide-*`
 * class entirely when `className` is passed rather than merging it, so a CSS
 * selector on the SVG class cannot tell one icon apart from another — this is
 * the stable hook tests use instead.
 */
function eventIcon(entry: BookingAuditEntry) {
  const cls = 'text-stone-500 dark:text-stone-300';
  const testId = `event-icon-${entry.eventType}`;
  if (entry.eventType === 'refund_issued')
    return <DollarSign size={16} className={cls} data-testid={testId} />;
  if (entry.eventType === 'rebooked')
    return <RefreshCw size={16} className={cls} data-testid={testId} />;
  // Without this branch a cancellation falls through to the plus-circle below —
  // the CREATION icon — so the row would read "Booking cancelled" beside a mark
  // meaning the opposite.
  if (entry.eventType === 'booking_cancelled')
    return <XCircle size={16} className={cls} data-testid={testId} />;
  if (entry.eventType === 'confirmation_resent' || entry.eventType === 'email_sent')
    return <Mail size={16} className={cls} data-testid={testId} />;
  // Distinct from the plain-Mail resent icon — a failed resend still gets its
  // own row (the audit is the only record of the attempt), and the two must
  // not read the same at a glance.
  if (entry.eventType === 'confirmation_resend_failed' || entry.eventType === 'email_failed')
    return <MailWarning size={16} className={cls} data-testid={testId} />;
  return <PlusCircle size={16} className={cls} data-testid="event-icon-fallback" />;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** For a `rebooked` event, resolve the OTHER end of the chain relative to the
 *  booking being viewed, so the row can link to it. The original booking is the
 *  superseded source (`fromBookingId`); the resulting booking is the new
 *  CONFIRMED one (`toBookingId`). */
function rebookCounterpart(
  entry: BookingAuditEntry,
  currentBookingId: string,
): { to: string; label: string } | null {
  if (entry.eventType !== 'rebooked') return null;
  const c = (entry.context ?? {}) as RebookedContext;
  if (c.fromBookingId === currentBookingId && c.toBookingId) {
    return { to: `/admin/bookings/${c.toBookingId}`, label: 'View resulting booking' };
  }
  if (c.toBookingId === currentBookingId && c.fromBookingId) {
    return { to: `/admin/bookings/${c.fromBookingId}`, label: 'View original booking' };
  }
  // Event not anchored to this booking (or ambiguous): prefer the original.
  if (c.fromBookingId)
    return { to: `/admin/bookings/${c.fromBookingId}`, label: 'View original booking' };
  if (c.toBookingId)
    return { to: `/admin/bookings/${c.toBookingId}`, label: 'View resulting booking' };
  return null;
}

/** For a `refund_issued` event, the minted gift card to link back to — set
 *  only on the gift-card branch (BON-147/BON-335). Follows
 *  `GiftCardBookingRefLink`'s rule: never a dead link, so absent `giftCardId`
 *  renders nothing rather than a link that 404s. */
function refundGiftCardLink(entry: BookingAuditEntry): { to: string; label: string } | null {
  if (entry.eventType !== 'refund_issued') return null;
  const c = (entry.context ?? {}) as RefundIssuedContext;
  if (!c.giftCardId) return null;
  return { to: `/admin/gift-cards/${c.giftCardId}`, label: 'View gift card' };
}

/** The one place a `refund_issued` row can point somewhere else — a rebook
 *  and a refund never share a row, so these two are mutually exclusive. */
function auditLink(entry: BookingAuditEntry, currentBookingId: string) {
  return rebookCounterpart(entry, currentBookingId) ?? refundGiftCardLink(entry);
}

/** The external reference (a Stripe refund id, a cheque number, a transfer
 *  id) a CSR gave when recording a card/other-rail refund (BON-335's B3
 *  audit-context field, previously written but never rendered). Set only on
 *  that branch — a gift-card refund's context never carries one. */
function refundReference(entry: BookingAuditEntry): string | null {
  if (entry.eventType !== 'refund_issued') return null;
  const c = (entry.context ?? {}) as RefundIssuedContext;
  return c.externalReference || null;
}

function auditRow(entry: BookingAuditEntry, currentBookingId: string) {
  const link = auditLink(entry, currentBookingId);
  const reference = refundReference(entry);
  return (
    <li key={entry.id} className="flex items-start gap-3">
      <span className="mt-0.5 flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100 dark:bg-stone-700/50">
        {eventIcon(entry)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="block text-sm font-medium text-bark-900 dark:text-stone-100">
            {entry.summary}
          </span>
          {entry.overrideUsed && (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <ShieldAlert size={11} /> Override
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs text-stone-500 dark:text-stone-400">
          {entry.actorName} · {formatWhen(entry.createdAt)}
        </span>
        {reference && (
          <span className="mt-0.5 block text-xs text-stone-500 dark:text-stone-400">
            Reference: <span className="font-mono">{reference}</span>
          </span>
        )}
        {link && (
          <Link
            to={link.to}
            className="mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-moss-600 hover:underline dark:text-moss-400"
          >
            {link.label}
            <ArrowUpRight size={12} />
          </Link>
        )}
      </span>
    </li>
  );
}

export function BookingAuditTimeline({
  bookingId,
  refreshToken = 0,
}: {
  bookingId: string;
  /**
   * Bump this (e.g. to a counter incremented once per completed action) to
   * force a refetch without waiting for `bookingId` to change. General
   * rather than refund-specific: a refund lands, the booking updates, but
   * this timeline had no way to hear about it — the same gap a future
   * rebook completion would hit. The caller owns what "an action completed"
   * means; this component only reacts to the value changing.
   */
  refreshToken?: number;
}) {
  const [entries, setEntries] = useState<BookingAuditEntry[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setError('');
    fetchBookingAudit(bookingId)
      .then((e) => {
        if (!cancelled) setEntries(e);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load activity');
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, refreshToken]);

  return (
    <div className={cardCls}>
      <div className="border-b border-stone-200 px-5 py-4 dark:border-stone-700">
        <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100">Activity</h2>
      </div>
      <div className="p-4">
        {error && <p className="text-xs text-red-700 dark:text-red-300">{error}</p>}
        {!error && entries === null && <p className="text-xs text-stone-400">Loading…</p>}
        {!error && entries !== null && entries.length === 0 && (
          <p className="text-xs text-stone-400">No recorded actions yet.</p>
        )}
        {!error && entries !== null && entries.length > 0 && (
          <ul className="space-y-3.5">{entries.map((e) => auditRow(e, bookingId))}</ul>
        )}
      </div>
    </div>
  );
}
